import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const STATUS_COLORS = { pending: "#f59e0b", review: "#8b5cf6", approved: "#10b981", rejected: "#ef4444", cancelled: "#6b7280" };
const STATUS_BG    = { pending: "#fffbeb", review: "#f5f3ff", approved: "#f0fdf4", rejected: "#fef2f2", cancelled: "#f9fafb" };

const S = {
  page: { padding: "0 0 80px 0", fontFamily: "system-ui, sans-serif", background: "#f5f5f5", minHeight: "100vh" },
  header: { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { margin: 0, fontSize: "1.1rem", fontWeight: 700 },
  back: { color: "#fff", textDecoration: "none", fontSize: "0.85rem", opacity: 0.9 },
  tabs: { display: "flex", gap: "0.4rem", padding: "0.75rem 1rem", overflowX: "auto", background: "#fff", borderBottom: "1px solid #e5e7eb" },
  tab: (active) => ({ padding: "0.35rem 0.9rem", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem", background: active ? "#667eea" : "#f3f4f6", color: active ? "#fff" : "#374151", whiteSpace: "nowrap" }),
  toast: (ok) => ({ margin: "0.75rem 1rem 0", padding: "0.7rem 1rem", borderRadius: 8, background: ok ? "#d1fae5" : "#fee2e2", color: ok ? "#065f46" : "#991b1b", fontSize: "0.85rem", display: "flex", justifyContent: "space-between" }),
  list: { padding: "0.75rem 1rem" },
  card: (status) => ({ background: STATUS_BG[status] || "#fff", borderRadius: 12, padding: "1rem", marginBottom: "0.75rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderLeft: `4px solid ${STATUS_COLORS[status] || "#ddd"}` }),
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" },
  amount: { fontWeight: 800, fontSize: "1.1rem", color: "#dc2626" },
  badge: (status) => ({ padding: "0.15rem 0.6rem", borderRadius: 12, fontSize: "0.68rem", fontWeight: 700, background: STATUS_COLORS[status] + "22", color: STATUS_COLORS[status] }),
  info: { display: "flex", flexDirection: "column", gap: "0.2rem", marginBottom: "0.5rem" },
  infoRow: { fontSize: "0.75rem", color: "#6b7280" },
  val: { color: "#374151", fontWeight: 500 },
  sendTo: { fontSize: "0.72rem", color: "#374151", background: "#f3f4f6", padding: "0.3rem 0.5rem", borderRadius: 6, fontFamily: "monospace", wordBreak: "break-all", marginBottom: "0.6rem" },
  btnRow: { display: "flex", gap: "0.5rem" },
  approveBtn: { flex: 1, padding: "0.45rem", borderRadius: 6, border: "none", background: "#10b981", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" },
  rejectBtn: { flex: 1, padding: "0.45rem", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" },
  empty: { textAlign: "center", color: "#9ca3af", padding: "2rem" },
};

function WithdrawalsManagement() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("Rejected by admin");
  const adminToken = localStorage.getItem("adminToken");

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API_URL}/admin/withdrawals${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.withdrawals || [];
      setWithdrawals(data);
    } catch { setMessage("❌ Failed to load withdrawals"); }
    finally { setLoading(false); }
  }, [filter, adminToken]);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this withdrawal?")) return;
    try {
      const res = await axios.post(`${API_URL}/admin/withdrawals/${id}/approve`, {},
        { headers: { Authorization: `Bearer ${adminToken}` } });
      setMessage(`✅ Approved! New balance: ${parseFloat(res.data.new_balance || 0).toFixed(2)} Birr`);
      fetchWithdrawals();
    } catch (err) { setMessage("❌ " + (err.response?.data?.detail || "Approval failed")); }
  };

  const handleRejectSubmit = async () => {
    try {
      await axios.post(
        `${API_URL}/admin/withdrawals/${rejectId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {}, { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMessage("✅ Withdrawal rejected and funds refunded");
      setRejectId(null);
      fetchWithdrawals();
    } catch (err) { setMessage("❌ " + (err.response?.data?.detail || "Rejection failed")); }
  };

  const pendingCount = withdrawals.filter(w => w.status === "pending").length;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>📤 Withdrawals {filter === "pending" && pendingCount > 0 && <span style={{ background: "#ef4444", borderRadius: 10, padding: "0 6px", fontSize: "0.75rem", marginLeft: 6 }}>{pendingCount}</span>}</h1>
        <Link to="/dashboard" style={S.back}>← Back</Link>
      </header>

      <div style={S.tabs}>
        {["pending", "review", "approved", "rejected", "all"].map((s) => (
          <button key={s} style={S.tab(filter === s)} onClick={() => setFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button style={S.tab(false)} onClick={fetchWithdrawals}>🔄</button>
      </div>

      {message && (
        <div style={S.toast(message.startsWith("✅"))}>
          <span>{message}</span>
          <button onClick={() => setMessage("")} style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
      )}

      {rejectId && (
        <div style={{ margin: "0.75rem 1rem", background: "#fff", borderRadius: 10, padding: "1rem", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.5rem", color: "#ef4444" }}>Reject Withdrawal #{rejectId}</div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{ width: "100%", padding: "0.5rem", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: "0.85rem", boxSizing: "border-box", minHeight: 60 }}
          />
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button onClick={handleRejectSubmit} style={{ ...S.rejectBtn, flex: 2 }}>Confirm Reject</button>
            <button onClick={() => setRejectId(null)} style={{ flex: 1, padding: "0.45rem", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={S.list}>
        {loading && <div style={S.empty}>Loading...</div>}
        {!loading && withdrawals.length === 0 && <div style={S.empty}>No {filter} withdrawals.</div>}
        {!loading && withdrawals.map((w) => (
          <div key={w.id} style={S.card(w.status)}>
            <div style={S.cardTop}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{w.username || "Unknown"}</div>
                <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>#{w.id} · {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={S.amount}>{parseFloat(w.amount).toFixed(2)} Birr</div>
                <span style={S.badge(w.status)}>{w.status}</span>
              </div>
            </div>
            <div style={S.info}>
              <span style={S.infoRow}>📱 Phone: <span style={S.val}>{w.phone || "—"}</span></span>
              <span style={S.infoRow}>🆔 Telegram ID: <span style={S.val}>{w.telegram_id || "—"}</span></span>
              <span style={S.infoRow}>💳 Method: <span style={S.val}>{w.payment_method || w.wallet_address || "—"}</span></span>
              <span style={S.infoRow}>👛 Source: <span style={S.val}>{w.wallet_source === "promo" ? "🎁 Promo (Best Player)" : "Real"}</span></span>
              {w.wallet_source === "promo" && (
                <span style={S.infoRow}>🎁 Promo part: <span style={S.val}>{parseFloat(w.promo_amount || 0).toFixed(2)} Birr · Real part: {(parseFloat(w.amount) - parseFloat(w.promo_amount || 0)).toFixed(2)} Birr</span></span>
              )}
            </div>
            {w.wallet_address && (
              <div style={S.sendTo}>Send to: {w.wallet_address}</div>
            )}
            {w.notes && <div style={{ fontSize: "0.72rem", color: "#6b7280", marginBottom: "0.5rem" }}>Note: {w.notes}</div>}
            {(w.status === "pending" || w.status === "review") && (
              <div style={S.btnRow}>
                <button style={S.approveBtn} onClick={() => handleApprove(w.id)}>✓ Approve</button>
                <button style={S.rejectBtn} onClick={() => { setRejectId(w.id); setRejectReason("Rejected by admin"); }}>✕ Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default WithdrawalsManagement;
